import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { setLoader } from './loader';
import { setEventData } from './events';
import { fetchWrapper } from '../utils';
import { setError } from './error';
import { setAnalyticsUserId } from '../analytics/common';
import { setAmplitudeUserProperties } from '../analytics/amplitude';

const {
  REACT_APP_API_URL,
  REACT_APP_STRIPE_PK,
  REACT_APP_STRIPE_NAME,
} = process.env;

export const signupSlice = createSlice({
  name: 'signup',
  initialState: {
    email: '',
    userName: '',
    isValid: false,
    token: null,
    flUserID: null,
    isSuccess: null,
    userUuid: null,
    stripePublishKey: REACT_APP_STRIPE_PK,
    stripeAccountName: REACT_APP_STRIPE_NAME,
    deeplinkToken: null,
    btToken: null,
    paymentSettingsStatus: null,
    paymentProvider: null,
  },
  reducers: {
    setEmail: (state, action) => {
      state.email = action.payload.email;
      state.isValid = action.payload.isValid
    },
    setRequestResult: (state, action) => {
      const {
        access_token,
        entity: {
          external_user_id,
          name,
          registration_source,
          email,
        },
      } = action.payload;
      state.token = access_token;
      state.isSuccess = true;
      state.flUserID = external_user_id;
      state.userName = name === email ? '' : name;
      state.email = email;
      state.registrationSource = registration_source
    },
    setUserToken: (state, action) => {
      state.token = action.payload
    },
    setUserUuid: (state, action) => {
      state.userUuid = action.payload
    },
    setDeeplinkToken: (state,action) => {
      state.deeplinkToken = action.payload
    },
    setStripeData: (state, action) => {
      state.stripePublishKey = action.payload.stripePublishKey;
      state.stripeAccountName = action.payload.stripeAccountName
    },
    setBtToken: (state, action) => {
      state.btToken = action.payload;
    },
    setPaymentSettingStatus: (state, action) => {
      state.paymentSettingsStatus = action.payload;
    },
    setPaymentProvider: (state, action) => {
      state.paymentProvider = action.payload;
    },
  },
});

export const {
  setEmail,
  setRequestResult,
  setUserToken,
  setUserUuid,
  setDeeplinkToken,
  setStripeData,
  setBtToken,
  setPaymentSettingStatus,
  setPaymentProvider,
} = signupSlice.actions;

export const sendAPIRequest = ({ email, name, landingType, userUuid }) => (dispatch) => {
  dispatch(setLoader(true));

  const params = {
    registration_source: 'email',
    name: name || email,
    email,
    landing_type: landingType,
    user_uuid: userUuid,
  };

  fetchWrapper(`${REACT_APP_API_URL}/signup`, { body: params })
    .then((data) => {
      window.sessionStorage.setItem('access_token', data.data.access_token);
      dispatch(setRequestResult(data.data));
      dispatch(setDeeplinkToken(data.data.deeplink_token));
      dispatch(getPaymentSettings(data.data.access_token));
      setAnalyticsUserId(data.data.entity.external_user_id);
    })
    .catch((error) => {
      dispatch(
        setError({
          show: true,
          text: error.errors?.fields?.email || error.errors?.messages || 'Failed to create an account',
          type: 'signup',
        })
      )
      dispatch(setLoader(false));
      // dispatch(setRequestResult({isSuccess: false}));
    })
};

const getPaymentSettings = (token, forceUseBT, attemptNum = 0) => (dispatch) => {
  let stripeParams = {
      stripePublishKey: REACT_APP_STRIPE_PK,
      stripeAccountName: REACT_APP_STRIPE_NAME,
    },
    status = null,
    paymentGateway = null;

  fetchWrapper(`${REACT_APP_API_URL}/users/me/payments-settings`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((data) => {
      const {
        stripe_publishable_key,
        stripe_account_name,
        braintree_client_token,
        payment_gateway,
      } = data;

      paymentGateway = braintree_client_token && payment_gateway ? payment_gateway : 'stripe';
      dispatch(setBtToken(braintree_client_token || null));
      status = 'success';

      if (data.experiments?.force_3ds_for_eu_customers) {
        setAmplitudeUserProperties({ '3Ds_test': data.experiments?.force_3ds_for_eu_customers || null });
      }
      if (stripe_publishable_key) {
        stripeParams = {
          stripePublishKey: stripe_publishable_key,
          stripeAccountName: stripe_account_name,
        };
      }
    })
    .catch((error) => {
      console.log(error);
      if (attemptNum < 2) {
        setTimeout(() => dispatch(getPaymentSettings(token, ++attemptNum)), 1000);
      } else {
        status = 'fail';
        paymentGateway = 'stripe';
      }
    })
    .then(() => {
      dispatch(setPaymentProvider(paymentGateway));
      dispatch(setStripeData(stripeParams));
      dispatch(setPaymentSettingStatus(status));
    })
    .then(() => dispatch(setLoader(false)));
};

export const setUuid = (userUuid) => (dispatch) => {
  const cookieUuid = Cookies.get('userUuid');

  if (!userUuid) {
    if (cookieUuid) {
      userUuid = cookieUuid;
    } else {
      userUuid = uuidv4();
    }
  }

  if (!cookieUuid) Cookies.set('userUuid', userUuid, { expires: 365 });
  dispatch(setUserUuid(userUuid));
  dispatch(setEventData({ id: 'userUuid', data: cookieUuid }));
  setAmplitudeUserProperties({ uuid: userUuid });
};

export const selectEmail = (state) => state.signup.email;
export const selectValidity = (state) => state.signup.isValid;
export const selectSuccess = (state) => state.signup.isSuccess;
export const selectToken = (state) => state.signup.token;
export const selectDeeplinkToken = (state) => state.signup.deeplinkToken;
export const selectUserID = (state) => state.signup.flUserID;
export const selectUserName = (state) => state.signup.userName;
export const selectUserUuid = (state) => state.signup.userUuid;
export const selectStripePublishKey = (state) => state.signup.stripePublishKey;
export const selectStripeAccountName = (state) => state.signup.stripeAccountName;
export const selectBtToken = state => state.signup.btToken;
export const selectPaymentSettingsStatus = (state) => state.signup.paymentSettingsStatus;
export const selectPaymentProvider = state => state.signup.paymentProvider;

export default signupSlice.reducer
